// navItems.ts — the data model for the unified nav.
//
// Each label / desc / info is bilingual. Phosphor icon component names are
// referenced as strings here; NavBar.tsx maps them to actual components from
// @phosphor-icons/react. Keep this file pure data so it's easy to edit copy.

import { Route } from "./useRoute";

export type BilingualField = { en: string; ko: string };

export type NavItemDef = {
  /** Stable id — primary items map 1:1 onto Route values. */
  id: Route | "language" | "signout";
  /** Phosphor icon component name, e.g. "Flask", "Database". */
  icon: PhosphorIconName;
  /** Accent color for active/hover state. */
  color: string;
  label: BilingualField;
  /** Short tooltip subtitle. Pass null to render title-only (Sign Out). */
  desc: BilingualField | null;
  /** Optional longer description shown in the info modal. Triggers the
   *  on-corner (i) badge when present. */
  info?: BilingualField;
  /** Yellow disclaimer callout shown inside the modal. */
  warning?: BilingualField;
  /** Visual grouping — child items render butted against their parent. */
  parent?: Route;
  /** Hide unless useAuth().isAdmin. */
  adminOnly?: boolean;
};

export type PhosphorIconName =
  | "RocketLaunch" | "Flask" | "Package" | "ChartBar" | "Database"
  | "Grains" | "Notebook" | "Translate" | "UserCircle" | "ShieldStar" | "SignOut"
  | "Scales";

export const PRIMARY_ITEMS: NavItemDef[] = [
  {
    id: "launchpad",
    icon: "RocketLaunch",
    color: "#a78bfa",
    label: { en: "Launch Pad",           ko: "런치패드" },
    desc:  { en: "Portal home — pick a tool", ko: "포털 홈 — 도구 선택" },
  },
  {
    id: "app",
    icon: "Flask",
    color: "#06b6d4",
    label: { en: "App",                  ko: "앱" },
    desc:  { en: "PDCAAS and Nutritionals", ko: "PDCAAS 및 영양 정보" },
    info: {
      en: "Load any formula then play with various modifications to achieve an optimized PDCAAS or claims value. Modify any recipe property including mathematical models for changes during processing. Generate US FDA nutrition facts panel. Explore claimable attributes.",
      ko: "배합을 불러온 다음 다양한 수정을 시도하여 최적화된 PDCAAS 또는 클레임 값을 달성하세요. 가공 중 변화에 대한 수학적 모델을 포함한 모든 레시피 속성을 수정할 수 있습니다. 미국 FDA 영양 성분표를 생성하고 클레임 가능한 속성을 탐색하세요.",
    },
    warning: {
      en: "Label gen is an R&D tool for design — do not place on final art without validation.",
      ko: "라벨 생성기는 R&D 디자인 도구이며 검증 없이 최종 아트워크에 사용하지 마세요.",
    },
  },
  {
    id: "packaging",
    icon: "Package",
    color: "#ec4899",
    parent: "app",
    label: { en: "Packaging Dashboard",  ko: "포장 대시보드" },
    desc:  { en: "Front of Pack & Cost of Goods", ko: "전면 표시 및 원가" },
    info: {
      en: "Finalize your NFP from the App with specifications on package net weight and servings. Visualize label components. View cost of goods breakdown.",
      ko: "포장 순중량 및 제공량 사양을 포함하여 앱에서 NFP를 완성하세요. 라벨 구성 요소를 시각화하고 원가 분석을 확인하세요.",
    },
    warning: {
      en: "Label gen is an R&D tool for design — do not place on final art without validation.",
      ko: "라벨 생성기는 R&D 디자인 도구이며 검증 없이 최종 아트워크에 사용하지 마세요.",
    },
  },
  {
    id: "compare",
    icon: "ChartBar",
    color: "#f59e0b",
    label: { en: "Compare",              ko: "비교" },
    desc:  { en: "Compare 2 or more formulas", ko: "2개 이상의 배합 비교" },
    info: {
      en: "Compare 2 or more formulas on nutrition facts, extended nutritionals, protein completeness, claimable attributes, clean-label factors and more. Visualize clinical potential with charts comparing interesting criteria selected based on alignment with real clinical trials. Use the AI agent to generate a report on how the recipes hold up against one another on various eval frameworks.",
      ko: "영양 성분, 확장 영양 정보, 단백질 완전성, 클레임 가능한 속성, 클린 라벨 요소 등에서 2개 이상의 배합을 비교하세요. 실제 임상 시험과의 일치를 기준으로 선택된 지표를 차트로 시각화해 임상 가능성을 살펴보세요. AI 에이전트를 사용하여 다양한 평가 프레임워크에서 레시피가 어떻게 비교되는지 보고서를 생성하세요.",
    },
  },
  {
    id: "goldenratio",
    icon: "Scales",
    color: "#f0abfc",
    parent: "compare",
    label: { en: "Golden Ratio",         ko: "골든 비율" },
    desc:  { en: "Protein blend vs target", ko: "단백질 배합 대 목표" },
  },
  {
    id: "database",
    icon: "Database",
    color: "#38bdf8",
    label: { en: "Database Home",        ko: "데이터베이스 홈" },
    desc:  { en: "Library overview",     ko: "라이브러리 개요" },
  },
  {
    id: "ingredients",
    icon: "Grains",
    color: "#34d399",
    parent: "database",
    label: { en: "Ingredients Overview", ko: "원료 전체보기" },
    desc:  { en: "Browse, edit, create ingredients", ko: "원료 탐색, 편집, 생성" },
  },
  {
    id: "formulas",
    icon: "Notebook",
    color: "#c084fc",
    parent: "database",
    label: { en: "Formula Overview",     ko: "배합 전체보기" },
    desc:  { en: "View and create formulas, record results", ko: "배합 보기 및 생성, 결과 기록" },
  },
];

export const META_ITEMS: NavItemDef[] = [
  {
    id: "language",
    icon: "Translate",
    color: "#2dd4bf",
    label: { en: "Switch Language",      ko: "언어 전환" },
    desc:  { en: "English ↔ 한국어",      ko: "한국어 ↔ English" },
  },
  {
    id: "account",
    icon: "UserCircle",
    color: "#818cf8",
    label: { en: "My Account",           ko: "내 계정" },
    desc:  { en: "Profile & preferences", ko: "프로필 및 환경설정" },
  },
  {
    id: "admin",
    icon: "ShieldStar",
    color: "#fcd34d",
    adminOnly: true,
    label: { en: "Admin",                ko: "관리자" },
    desc:  { en: "Users, invitations, activity log", ko: "사용자, 초대장, 활동 로그" },
  },
  {
    id: "signout",
    icon: "SignOut",
    color: "#fb7185",
    label: { en: "Sign Out",             ko: "로그아웃" },
    desc: null,
  },
];

/** Pick the localized string off a bilingual field. */
export function tr(field: BilingualField | null | undefined, lang: "en" | "ko"): string {
  if (!field) return "";
  return field[lang] || field.en || "";
}
